import React, { useState, useEffect } from "react";
import { Card } from "flowbite-react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useAuth } from "../../../contexts/AuthContext";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import RIASECHexagonChart from "./RIASECHexagonChart"; // <-- Our new radar chart
import Hexagon from "./Hexagon"; // The existing static hexagon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

// ========== QUESTIONS SETUP ==========
const realisticQuestions = [
  { id: 0, text: "I truly enjoy working with my hands and practical tools.", category: "R" },
  { id: 1, text: "I like fixing broken items with my own hands.", category: "R" },
  { id: 2, text: "I enjoy building structures and assembling things from scratch.", category: "R" },
  { id: 3, text: "I prefer hands-on tasks that involve practical work.", category: "R" },
  { id: 4, text: "I like outdoor work using machinery and physical tools.", category: "R" },
  { id: 5, text: "I feel energized by performing physical and manual tasks.", category: "R" },
  { id: 6, text: "I appreciate crafts that require skill and precise work.", category: "R" },
  { id: 7, text: "I excel in tasks needing mechanical skills and attention.", category: "R" },
  { id: 8, text: "I enjoy troubleshooting equipment and solving technical issues.", category: "R" },
  { id: 9, text: "I like designing and assembling projects using my hands.", category: "R" },
];

const investigativeQuestions = [
  { id: 10, text: "I enjoy solving complex problems using logical thinking skills.", category: "I" },
  { id: 11, text: "I like researching data and analyzing information carefully.", category: "I" },
  { id: 12, text: "I am curious about how everyday objects actually work.", category: "I" },
  { id: 13, text: "I prefer tasks that involve deep and critical thinking.", category: "I" },
  { id: 14, text: "I am drawn to scientific experiments and analytical research.", category: "I" },
  { id: 15, text: "I enjoy exploring innovative theories and solving puzzles.", category: "I" },
  { id: 16, text: "I like conducting experiments to test various interesting ideas.", category: "I" },
  { id: 17, text: "I excel at logical reasoning and systematic problem solving.", category: "I" },
  { id: 18, text: "I enjoy learning new concepts in science and research.", category: "I" },
  { id: 19, text: "I prefer tasks that challenge my intellect and curiosity.", category: "I" },
];

const artisticQuestions = [
  { id: 20, text: "I enjoy expressing creativity through art and projects.", category: "A" },
  { id: 21, text: "I appreciate art, music, and literature every day.", category: "A" },
  { id: 22, text: "I like experimenting with fresh ideas in artistic ways.", category: "A" },
  { id: 23, text: "I prefer free environments that foster creative expression.", category: "A" },
  { id: 24, text: "I enjoy tasks that allow creative self-expression in art.", category: "A" },
  { id: 25, text: "I love exploring various art forms and creative activities.", category: "A" },
  { id: 26, text: "I find inspiration in simple moments and creative ideas.", category: "A" },
  { id: 27, text: "I enjoy writing stories and creative pieces with passion.", category: "A" },
  { id: 28, text: "I am passionate about visual arts and creative design.", category: "A" },
  { id: 29, text: "I value creative freedom and unique artistic expression.", category: "A" },
];

const socialQuestions = [
  { id: 30, text: "I like helping others with their personal and social issues.", category: "S" },
  { id: 31, text: "I enjoy teaching people and sharing knowledge freely.", category: "S" },
  { id: 32, text: "I am drawn to volunteer work and community service.", category: "S" },
  { id: 33, text: "I prefer working in groups and collaborating with others.", category: "S" },
  { id: 34, text: "I am empathetic and connect well with those around me.", category: "S" },
  { id: 35, text: "I excel in teamwork and group-oriented social tasks.", category: "S" },
  { id: 36, text: "I enjoy mentoring others and offering supportive guidance.", category: "S" },
  { id: 37, text: "I find fulfillment in community involvement and social work.", category: "S" },
  { id: 38, text: "I value building strong relationships with peers and colleagues.", category: "S" },
  { id: 39, text: "I feel rewarded when I support others in their needs.", category: "S" },
];

const enterprisingQuestions = [
  { id: 40, text: "I enjoy leading teams and motivating others to succeed.", category: "E" },
  { id: 41, text: "I like promoting ideas and selling products confidently.", category: "E" },
  { id: 42, text: "I am comfortable taking risks and making bold decisions.", category: "E" },
  { id: 43, text: "I prefer competitive settings where ambition is highly valued.", category: "E" },
  { id: 44, text: "I am persuasive and enjoy convincing others with my ideas.", category: "E" },
  { id: 45, text: "I thrive in leadership roles and inspire team success.", category: "E" },
  { id: 46, text: "I enjoy setting ambitious goals and pursuing them relentlessly.", category: "E" },
  { id: 47, text: "I excel at inspiring groups and driving strategic initiatives.", category: "E" },
  { id: 48, text: "I am driven to succeed in various business ventures.", category: "E" },
  { id: 49, text: "I value initiative and strategic thinking in my actions.", category: "E" },
];

const conventionalQuestions = [
  { id: 50, text: "I enjoy organizing data and keeping information well-structured.", category: "C" },
  { id: 51, text: "I like following procedures and maintaining consistent routines.", category: "C" },
  { id: 52, text: "I prefer structured environments with clear, stable rules.", category: "C" },
  { id: 53, text: "I am detail-oriented and strive for accuracy in my work.", category: "C" },
  { id: 54, text: "I enjoy working with numbers and performing data analysis.", category: "C" },
  { id: 55, text: "I excel at maintaining order and systematic work methods.", category: "C" },
  { id: 56, text: "I appreciate well-planned approaches and organized task management.", category: "C" },
  { id: 57, text: "I find comfort in routines and structured daily schedules.", category: "C" },
  { id: 58, text: "I value precision and accuracy in every detail I work on.", category: "C" },
  { id: 59, text: "I enjoy tasks that require careful planning and organization.", category: "C" },
];

// Combine all questions
const questionsData = [
  ...realisticQuestions,
  ...investigativeQuestions,
  ...artisticQuestions,
  ...socialQuestions,
  ...enterprisingQuestions,
  ...conventionalQuestions,
];

// Survey constants
const totalQuestions = questionsData.length; // 60
const questionsPerPage = 6;
const totalPages = totalQuestions / questionsPerPage; // 10

// Map for categories -> array index
const categoryMap = { R: 0, I: 1, A: 2, S: 3, E: 4, C: 5 };

// Styles for selected/unselected answers
const selectedClasses = {
  1: "bg-red-400 border-red-400",
  2: "bg-orange-300 border-orange-300",
  3: "bg-gray-300 border-gray-300",
  4: "bg-green-300 border-green-300",
  5: "bg-green-500 border-green-500",
};

const unselectedClasses = {
  1: "bg-red-400/20 border-red-400",
  2: "bg-orange-300/20 border-orange-300",
  3: "bg-gray-300/20 border-gray-300",
  4: "bg-green-300/20 border-green-300",
  5: "bg-green-500/20 border-green-500",
};

function CareerInterestSurvey() {
  const { currentUser } = useAuth();

  // We store:
  //   userSurveyData: { answers, finished, careerSurveyResults }
  //   answers: local array for the user’s answers
  const [userSurveyData, setUserSurveyData] = useState(null);
  const [loadingSurveyData, setLoadingSurveyData] = useState(true);

  // Has the user decided to start/resume the actual question flow
  const [surveyStarted, setSurveyStarted] = useState(false);

  // Local array of answers (1-5 for each question, or null)
  const [answers, setAnswers] = useState(Array(totalQuestions).fill(null));

  const [currentPage, setCurrentPage] = useState(0);

  // Used during Firestore updates
  const [updating, setUpdating] = useState(false);

  // Load survey data from Firestore (including final results if any)
  useEffect(() => {
    if (currentUser) {
      const userDocRef = doc(db, "users", currentUser.uid);
      getDoc(userDocRef).then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          const existingAnswers = data.careerSurveyAnswers || [];
          const finished = data.careerSurveyFinished || false;
          const careerSurveyResults = data.careerSurveyResults || null;

          // Merge existingAnswers into full array of length 60
          const fullAnswers = Array(totalQuestions).fill(null);
          for (let i = 0; i < existingAnswers.length; i++) {
            fullAnswers[i] = existingAnswers[i];
          }

          setUserSurveyData({
            answers: fullAnswers,
            finished,
            careerSurveyResults,
          });
          setAnswers(fullAnswers);
        } else {
          // No doc means user is brand new
          setUserSurveyData({
            answers: Array(totalQuestions).fill(null),
            finished: false,
            careerSurveyResults: null,
          });
        }
        setLoadingSurveyData(false);
      });
    }
  }, [currentUser]);

  // Avoid rendering the next screens until we know user's status
  if (!surveyStarted && loadingSurveyData) {
    return null;
  }

  // ========== CASE 1: User has ALREADY FINISHED the survey ==========
  if (!surveyStarted && userSurveyData && userSurveyData.finished) {
    return (
      <Card className="px-2 mt-5">
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <h3 className="text-2xl font-bold mb-4">Discover Your Ideal Career Path</h3>
            <p className="mb-2">
              Your <span className="font-semibold">unique qualities</span> and{" "}
              <span className="font-semibold">interests</span> serve as a compass toward the
              career that best fits you. Uncover your <span className="font-semibold">strengths</span>,
              ignite your <span className="font-semibold">passions</span>, and chart a{" "}
              <span className="font-semibold">personalized</span> path.
            </p>
            <p className="mb-4">
              At SFU, you can explore innovative programs—from technology and business to the
              creative arts—that are designed to align with your aspirations. By linking your
              survey responses with SFU's expansive programs, you'll discover academic pathways
              crafted just for you.
            </p>
            <p className="mb-3">
              You've already completed the survey. Would you like to edit your responses or restart?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setCurrentPage(0);
                  setSurveyStarted(true);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                Edit Survey Answers
              </button>
              <button
                onClick={async () => {
                  const newAnswers = Array(totalQuestions).fill(null);
                  setAnswers(newAnswers);
                  if (currentUser) {
                    try {
                      const userDocRef = doc(db, "users", currentUser.uid);
                      await updateDoc(userDocRef, {
                        careerSurveyAnswers: newAnswers,
                        careerSurveyFinished: false,
                        careerSurveyResults: null,
                      });
                    } catch (error) {
                      console.error("Error resetting survey:", error);
                    }
                  }
                  setCurrentPage(0);
                  setSurveyStarted(true);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
              >
                Restart Survey
              </button>
            </div>
          </div>

          {/* Right column: Show the final RIASEC chart if we have results */}
          <div className="col-span-3 flex flex-col items-center justify-center">
            {userSurveyData.careerSurveyResults ? (
              <RIASECHexagonChart results={userSurveyData.careerSurveyResults} />
            ) : (
              <Hexagon /> // fallback if no results
            )}
          </div>
        </div>
      </Card>
    );
  }

  // ========== CASE 2: User has STARTED but not FINISHED the survey ==========
  if (!surveyStarted && userSurveyData && userSurveyData.answers.some((a) => a !== null)) {
    return (
      <Card className="px-2 mt-5">
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <h3 className="text-2xl font-bold mb-4">Discover Your Ideal Career Path</h3>
            <p className="mb-2">
              Your <span className="font-semibold">unique qualities</span> and{" "}
              <span className="font-semibold">interests</span> serve as a compass toward the
              career that best fits you. Uncover your{" "}
              <span className="font-semibold">strengths</span>, ignite your{" "}
              <span className="font-semibold">passions</span>, and explore a path tailored to
              your individuality.
            </p>
            <p className="mb-4">
              At SFU, you'll find diverse programs—from cutting-edge technology and business
              studies to the creative arts—that can guide you toward a career that resonates
              with your aspirations. By linking your survey responses with SFU's expansive
              programs, you'll discover academic pathways crafted just for you.
            </p>
            <p className="mb-3">
              You've already started the survey. Would you like to resume or restart from scratch?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setCurrentPage(0);
                  setSurveyStarted(true);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                Resume Survey
              </button>
              <button
                onClick={async () => {
                  const newAnswers = Array(totalQuestions).fill(null);
                  setAnswers(newAnswers);
                  if (currentUser) {
                    try {
                      const userDocRef = doc(db, "users", currentUser.uid);
                      await updateDoc(userDocRef, {
                        careerSurveyAnswers: newAnswers,
                        careerSurveyResults: null,
                        careerSurveyFinished: false,
                      });
                    } catch (error) {
                      console.error("Error resetting survey:", error);
                    }
                  }
                  setCurrentPage(0);
                  setSurveyStarted(true);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
              >
                Restart Survey
              </button>
            </div>
          </div>
          <div className="col-span-3 flex justify-center items-center">
            <Hexagon />
          </div>
        </div>
      </Card>
    );
  }

  // ========== CASE 3: BRAND NEW USER (no answers, not finished) ==========
  if (!surveyStarted) {
    return (
      <Card className="px-2 mt-5">
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <h3 className="text-2xl font-bold mb-4">Discover Your Ideal Career Path</h3>
            <p className="mb-2">
              Your <span className="font-semibold">unique qualities</span> and{" "}
              <span className="font-semibold">interests</span> serve as a compass toward finding
              a career that fits you perfectly. Take this quiz to uncover your hidden{" "}
              <span className="font-semibold">strengths</span> and ignite your{" "}
              <span className="font-semibold">passions</span>.
            </p>
            <p className="mb-4">
              At SFU, you can explore a wide range of programs—from innovative tech and business
              studies to the creative arts—all designed to help you pursue a career path that
              truly aligns with your <span className="font-semibold">personalized</span> aspirations.
              By linking your survey responses with SFU's expansive programs, you'll discover
              academic pathways crafted just for you.
            </p>
            <p className="mb-3 font-semibold">Ready to take the survey?</p>
            <button
              onClick={() => setSurveyStarted(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Begin Survey
            </button>
          </div>
          <div className="col-span-3 flex justify-center items-center">
            <Hexagon />
          </div>
        </div>
      </Card>
    );
  }

  // ========== SURVEY IN-PROGRESS (display questions) ==========
  const startIndex = currentPage * questionsPerPage;
  const currentQuestions = questionsData.slice(startIndex, startIndex + questionsPerPage);

  // Progress bar logic
  const progressPercent = (currentPage / totalPages) * 100;

  // Check if user answered all on current page
  const isCurrentPageComplete = currentQuestions.every((q) => answers[q.id] !== null);

  // Local answer handler
  const handleAnswer = (questionIndex, answerValue) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerValue;
    setAnswers(newAnswers);
  };

  // Save partial answers to Firestore
  const updateFirestoreAnswers = async () => {
    if (currentUser) {
      try {
        const userDocRef = doc(db, "users", currentUser.uid);
        await updateDoc(userDocRef, { careerSurveyAnswers: answers });
      } catch (error) {
        console.error("Error updating Firestore:", error);
      }
    }
  };

  const handleNext = async () => {
    setUpdating(true);
    await updateFirestoreAnswers();
    setUpdating(false);

    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Sum up categories
  const calculateResults = () => {
    const categoryScores = Array(6).fill(0);
    questionsData.forEach((q, idx) => {
      const answer = answers[idx];
      if (answer !== null) {
        const catIndex = categoryMap[q.category];
        categoryScores[catIndex] += (answer - 1);
      }
    });
    return categoryScores;
  };

  // Final Submit: compute, normalize, save
  const handleSubmit = async () => {
    setUpdating(true);
    try {
      const results = calculateResults();
      const totalScore = results.reduce((sum, val) => sum + val, 0);
      const normalizedResults =
        totalScore > 0 ? results.map((val) => val / totalScore) : results;

      // Format as object
      const normalizedResultsObj = {
        Realistic: normalizedResults[0],
        Investigative: normalizedResults[1],
        Artistic: normalizedResults[2],
        Social: normalizedResults[3],
        Enterprising: normalizedResults[4],
        Conventional: normalizedResults[5],
      };

      // Update Firestore
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        await updateDoc(userDocRef, {
          careerSurveyAnswers: answers,
          careerSurveyFinished: true,
          careerSurveyResults: normalizedResultsObj,
        });
      }

      // Store in userSurveyData state so next time we load we have it
      setUserSurveyData((prev) => ({
        ...prev,
        answers,
        finished: true,
        careerSurveyResults: normalizedResultsObj,
      }));

      // End the survey session
      setUpdating(false);
      setSurveyStarted(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error submitting survey:", err);
      setUpdating(false);
    }
  };

  // ========== RENDER THE ACTIVE SURVEY PAGE ==========
  return (
    <div className="mx-auto max-w-3xl px-4 pb-4 mt-3">
      <Card className="px-2 bg-gray-50">
        {/* Progress Bar */}
        <div className="mb-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-blue-700">
              {`${Math.round(progressPercent)}%`}
            </span>
            <span className="text-sm font-medium text-blue-700">
              {`Step ${currentPage + 1} of ${totalPages}`}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <h3 className="text-lg font-medium text-center mb-1">
          Choose how accurately each statement reflects you.
        </h3>

        {/* Scale legend */}
        <div className="flex justify-center space-x-12 mb-3">
          {[1, 2, 3, 4, 5].map((option) => (
            <div key={option} className="flex flex-col items-center text-sm">
              <div
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${unselectedClasses[option]}`}
              />
              <span className="mt-1 text-xs text-center">
                {option === 1 ? (
                  <>
                    Strongly <br /> disagree
                  </>
                ) : option === 2 ? (
                  "Disagree"
                ) : option === 3 ? (
                  "Neutral"
                ) : option === 4 ? (
                  "Agree"
                ) : (
                  <>
                    Strongly <br /> Agree
                  </>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Current page questions */}
        <div className="space-y-4 mb-6">
          {currentQuestions.map((q) => (
            <div
              key={q.id}
              className="text-center border border-gray-300 bg-white rounded pt-3 px-3 pb-4"
            >
              <p className="text-gray-800 mb-3">{q.text}</p>
              <div className="flex justify-center space-x-12">
                {[1, 2, 3, 4, 5].map((option) => {
                  const isSelected = answers[q.id] === option;
                  return (
                    <div
                      key={option}
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all cursor-pointer ${
                        isSelected
                          ? `${selectedClasses[option]} text-black`
                          : `${unselectedClasses[option]}`
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleAnswer(q.id, option)}
                        className="w-full h-full rounded-full flex items-center justify-center"
                      >
                        {isSelected && <FontAwesomeIcon className="text-gray-700 mt-0.5" icon={faCheck} style={{ fontSize: '1.35em' }} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Next or Submit */}
        <div>
          <button
            type="button"
            onClick={async () => {
              if (!isCurrentPageComplete) {
                toast.warning("Please answer all the questions before proceeding.");
                return;
              }
              if (currentPage < totalPages - 1) {
                await handleNext();
              } else {
                await handleSubmit();
              }
            }}
            className={`w-full px-4 py-2 rounded text-white transition-colors ${
              isCurrentPageComplete
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-600 opacity-50 cursor-not-allowed"
            }`}
          >
            {currentPage < totalPages - 1 ? "Next" : "Submit"}
          </button>
        </div>
      </Card>
    </div>
  );
}

export default CareerInterestSurvey;
